import React, {useState, useEffect, useMemo} from 'react';
import {Modal, Table, Button, message, Pagination, Space, Card, Statistic} from 'antd';
import {CopyOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';
import {PickupAppointmentType} from '../graphql/queries/getPickupAppointmentListing';
import {usePickupAppointmentQuery} from '../hooks/usePickupAppointmentQuery';
import {PickupAppointmentInitialValue} from '../graphql/queries/getPickupAppointmentListing';
import PickupAppointmentFilter from './PickupAppointmentFilter';
import MultiDateSelector from './MultiDateSelector';
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";
import {useMutation} from '@apollo/client';
import {COPY_PICKUP_APPOINTMENTS} from '../graphql/mutations/copyPickupAppointments';
import {packageUnitFilterOptions} from '../types/PickupAppointmentType';
import {useHashState} from 'utils/hashState';
import { useTranslation } from 'react-i18next';
import { useStatusInternationalization } from '../../../../pages/delivery/parcels/hooks/useStatusInternationalization';
import { ConsolidationTaskStatus, getStatusText } from 'pages/wms/consolidation/graphql/types/ConsolidationTypes';
import { PickupDataProvider } from '../../pickup-task/contexts/PickupDataContext';

interface PickupAppointmentCopyModalProps {
    visible: boolean;
    onClose: () => void;
    onSuccess: () => void;
    listPickupDate?: string; // 列表页面的筛选日期
}

interface CopyResultType {
    appointmentNumber: string;
    successCount: number;
    failureCount: number;
    failureReasons: string[];
}

const getYesNoText = (value: unknown, t: any) => {
    const isYes = value === 1 || value === true;
    const isNo = value === 0 || value === false;
    return isYes ? t('enum.yesorNoStatus.yes') : isNo ? t('enum.yesorNoStatus.no') : '--';
};

const PickupAppointmentCopyModal: React.FC<PickupAppointmentCopyModalProps> = ({
    visible,
    onClose,
    onSuccess,
    listPickupDate
}) => {
    const [tempFilters, setTempFilters] = useHashState(PickupAppointmentInitialValue);
    const [filters, setFilters] = useHashState(PickupAppointmentInitialValue);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [persistentSelection, setPersistentSelection] = useState<React.Key[]>([]);
    const [selectedAppointments, setSelectedAppointments] = useState<Map<React.Key, PickupAppointmentType>>(new Map());
    const [selectedDates, setSelectedDates] = useState<string[]>([]);
    const [showResult, setShowResult] = useState(false);
    const [copyResults, setCopyResults] = useState<CopyResultType[]>([]);
    const [step, setStep] = useState<'select' | 'confirm' | 'result'>('select');

    const { currentWarehouse } = useWarehouseContext();
    const {getPickupAppointmentSourceText,getPickupAppointmentStatusText} = useStatusInternationalization();
    // 使用自定义hook获取数据
    const {
        appointmentData,
        loading,
        refetch,
        totalElements,
        buildQueryVariables
    } = usePickupAppointmentQuery(filters, currentPage, pageSize, currentWarehouse?.id!);

    // 复制预约的mutation
    const [copyAppointments, {loading: copyLoading}] = useMutation(COPY_PICKUP_APPOINTMENTS);
    const {t} = useTranslation();
    // 初始化时设置为列表的pickup日期
    useEffect(() => {
        if (visible) {
            // 使用列表页面的筛选日期，如果没有则使用当前日期
            const pickupDate = listPickupDate || dayjs().format('YYYY-MM-DD');
            const pickupFilters = {
                ...PickupAppointmentInitialValue,
                expectedPickupDate: pickupDate
            };
            setFilters(pickupFilters);
            setTempFilters(pickupFilters);
            
            // 默认选择pickup日期的下一天作为复制目标日期
            const nextDay = dayjs(pickupDate).add(1, 'day').format('YYYY-MM-DD');
            setSelectedDates([nextDay]);
            
            setStep('select');
            setShowResult(false);
            // 清空选择
            setSelectedRowKeys([]);
            setPersistentSelection([]);
            setSelectedAppointments(new Map());
            setCopyResults([]);
        }
    }, [visible, listPickupDate]);

    // 监听状态变化后执行查询
    useEffect(() => {
        if (visible) {
            refetch(buildQueryVariables());
        }
    }, [filters, currentPage, pageSize, visible]);

    // 处理选择变化的函数，支持跨分页选择
    const handleSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: PickupAppointmentType[]) => {
        setSelectedRowKeys(newSelectedRowKeys);

        const newPersistentKeys = [...persistentSelection];
        const newSelectedAppointments = new Map(selectedAppointments);

        appointmentData.forEach(appointment => {
            const id = appointment.id;
            const isSelected = newSelectedRowKeys.includes(id);

            if (isSelected && !newPersistentKeys.includes(id)) {
                newPersistentKeys.push(id);
                newSelectedAppointments.set(id, appointment);
            }

            if (!isSelected && newPersistentKeys.includes(id)) {
                const index = newPersistentKeys.indexOf(id);
                if (index > -1) {
                    newPersistentKeys.splice(index, 1);
                    newSelectedAppointments.delete(id);
                }
            }
        });

        setPersistentSelection(newPersistentKeys);
        setSelectedAppointments(newSelectedAppointments);
    };

    // 当页面数据加载完成后，根据持久化选择更新当前页面的选择状态
    useEffect(() => {
        if (!loading && appointmentData.length > 0 && visible) {
            const currentPageKeys = appointmentData
                .filter(appointment => persistentSelection.includes(appointment.id))
                .map(appointment => appointment.id);
            setSelectedRowKeys(currentPageKeys);
        }
    }, [appointmentData, loading, persistentSelection, visible]);

    // 计算选中项的汇总信息
    const selectedSummary = useMemo(() => {
        if (selectedAppointments.size === 0) {
            return '';
        }

        const unitCounts = new Map<string, number>();
        selectedAppointments.forEach((appointment) => {
            const count = appointment.expectedParcelCount || 0;
            const unit = appointment.expectedParcelUnit || 'PIECE';
            const currentCount = unitCounts.get(unit) || 0;
            unitCounts.set(unit, currentCount + count);
        });

        const unitParts: string[] = [];
        packageUnitFilterOptions.forEach(option => {
            const count = unitCounts.get(option.value);
            if (count && count > 0) {
                unitParts.push(`${count} ${option.label}`);
            }
        });

        return unitParts.length > 0 ? unitParts.join(' + ') : '';
    }, [selectedAppointments]);

    const handleResetFilters = () => {
        // 重置时也要保持列表的pickup日期
        const pickupDate = listPickupDate || dayjs().format('YYYY-MM-DD');
        const resetFilters = {
            ...PickupAppointmentInitialValue,
            expectedPickupDate: pickupDate
        };
        setTempFilters(resetFilters);
        setFilters(resetFilters);
    };

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
    };

    const handleNextStep = () => {
        if (step === 'select') {
            if (persistentSelection.length === 0) {
                message.warning('Please select at least one appointment to copy.');
                return;
            }
            setStep('confirm');
        }
    };

    const handleBackStep = () => {
        if (step === 'confirm') {
            setStep('select');
        } else if (step === 'result') {
            setStep('select');
        }
    };

    const handleConfirmCopy = async () => {
        if (selectedDates.length === 0) {
            message.warning('Please select at least one date for copying.');
            return;
        }

        try {
            const appointmentIds = persistentSelection.map((key: React.Key) => Number(key));
            
            const result = await copyAppointments({
                variables: {
                    appointmentIds,
                    targetDates: selectedDates
                }
            });

            const copyResults = result.data?.copyPickupAppointments || [];
            setCopyResults(copyResults);
            setStep('result');
            
            message.success('Copy operation completed. Please check the results.');
        } catch (error) {
            message.error('Failed to copy appointments');
            console.error('Copy error:', error);
        }
    };

    const handleClose = () => {
        if (step === 'result') {
            onSuccess();
        }
        onClose();
    };

    const columns = [
        {
            title: t('pickup.appointment.copyAppointmentModal.appointmentNumber'),
            dataIndex: 'appointmentNumber',
            key: 'appointmentNumber',
            width: 180,
        },
        {
            title: t('pickup.appointment.copyAppointmentModal.customer'),
            dataIndex: 'channelName',
            key: 'channelName',
            width: 150,
        },
        {
            title: t('pickup.appointment.copyAppointmentModal.contactName'),
            dataIndex: 'contactName',
            key: 'contactName',
            width: 120,
        },
        {
            title: t('pickup.appointment.copyAppointmentModal.address'),
            key: 'address',
            width: 200,
            render: (record: PickupAppointmentType) => 
                `${record.addressLine1}${record.addressLine2 ? ', ' + record.addressLine2 : ''}`
        },
        {
            title: t('pickup.appointment.filters.source'),
            key: 'source',
            width: 150,
            render: ( record: PickupAppointmentType) => (
                <span className="text-sm">
                    {getPickupAppointmentSourceText(record.source)}
                </span>
            ),
        },
        {
            title: t('pickup.appointment.filters.isNew'),
            key: 'isNew',
            width: 80,
            render: (record: PickupAppointmentType) => (
                <span className="text-sm">
                    {getYesNoText((record as any).isNew, t)}
                </span>
            ),
        },
        {
            title: t('pickup.appointment.filters.continuousFailure'),
            key: 'continuousFailure',
            width: 120,
            render: (record: PickupAppointmentType) => (
                <span className="text-sm">
                    {getYesNoText((record as any).continuousFailure, t)}
                </span>
            ),
        },
        {
            title: t('pickup.appointment.copyAppointmentModal.zipcode'),
            dataIndex: 'zipcode',
            key: 'zipcode',
            width: 100,
        },
        {
            title: t('pickup.appointment.copyAppointmentModal.expectedTime'),
            key: 'expectedTime',
            width: 150,
            render: (record: PickupAppointmentType) => 
                `${record.expectedPickupTimeStart} ~ ${record.expectedPickupTimeEnd}`
        },
        {
            title: t('pickup.appointment.copyAppointmentModal.expectedCount'),
            key: 'expectedCount',
            width: 120,
            render: (record: PickupAppointmentType) => 
                `${record.expectedParcelCount} ${record.expectedParcelUnit}`
        },
        {
            title: t('pickup.appointment.filters.confirm'),
            key: 'confirm',
            width: 120,
            render: (record: PickupAppointmentType) => (
                <span className="text-sm">
                    {record.confirm ? t('enum.yesorNoStatus.yes') : !record.confirm ? t('enum.yesorNoStatus.no') : '--'}
                </span>
            ),
        },
        {
            title: t('pickup.appointment.copyAppointmentModal.status'),
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status: string) => (
                <span className="text-sm">
                    {getPickupAppointmentStatusText(status)}
                </span>
            ),
        },
    ];

    const resultColumns = [
        {
            title: t('pickup.appointment.copyAppointmentModal.appointmentNumber'),
            dataIndex: 'appointmentNumber',
            key: 'appointmentNumber',
            width: 180,
        },
        {
            title: t('pickup.appointment.copyAppointmentModal.successCount'),
            dataIndex: 'successCount',
            key: 'successCount',
            width: 120,
            render: (count: number) => (
                <span style={{color: '#52c41a'}}>{count}</span>
            )
        },
        {
            title: t('pickup.appointment.copyAppointmentModal.failureCount'),
            dataIndex: 'failureCount',
            key: 'failureCount',
            width: 120,
            render: (count: number) => (
                <span style={{color: count > 0 ? '#ff4d4f' : '#52c41a'}}>{count}</span>
            )
        },
        {
            title: t('pickup.appointment.copyAppointmentModal.failureReasons'),
            dataIndex: 'failureReasons',
            key: 'failureReasons',
            render: (reasons: string[]) => (
                <div>
                    {reasons.map((reason, index) => (
                        <div key={index} style={{color: '#ff4d4f', fontSize: '12px'}}>
                            {reason}
                        </div>
                    ))}
                </div>
            )
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: handleSelectChange,
        preserveSelectedRowKeys: true,
    };

    return (
        <PickupDataProvider>
            <Modal
                title={
                    <div className="flex items-center">
                        <CopyOutlined className="mr-2" />
                        <span>{t('pickup.appointment.copyAppointmentModal.title')}</span>
                        {step === 'confirm' && <span className="ml-2 text-gray-500">- Confirm Copy</span>}
                        {step === 'result' && <span className="ml-2 text-gray-500">- Copy Results</span>}
                    </div>
                }
                open={visible}
                onCancel={handleClose}
                width={step === 'result' ? 800 : 1200}
                footer={
                <div className="flex justify-between">
                    {step !== 'select' && (
                        <Button onClick={handleBackStep}>
                            {t('pickup.appointment.copyAppointmentModal.back')}
                        </Button>
                    )}
                    <div className="flex space-x-2">
                        {step === 'select' && (
                            <>
                                <Button onClick={onClose}>
                                    {t('pickup.appointment.copyAppointmentModal.cancel')}
                                </Button>
                                <Button 
                                    type="primary" 
                                    onClick={handleNextStep}
                                    disabled={persistentSelection.length === 0}
                                >
                                    {t('pickup.appointment.copyAppointmentModal.next', {count: persistentSelection.length})}
                                </Button>
                            </>
                        )}
                        {step === 'confirm' && (
                            <>
                                <Button onClick={handleBackStep}>
                                    {t('pickup.appointment.copyAppointmentModal.cancel')}
                                </Button>
                                <Button 
                                    type="primary" 
                                    onClick={handleConfirmCopy}
                                    loading={copyLoading}
                                    disabled={selectedDates.length === 0}
                                >
                                    {t('pickup.appointment.copyAppointmentModal.confirmCopy')}
                                </Button>
                            </>
                        )}
                        {step === 'result' && (
                            <Button type="primary" onClick={handleClose}>
                                {t('pickup.appointment.copyAppointmentModal.close')}
                            </Button>
                        )}
                    </div>
                </div>
            }
        >
            {step === 'select' && (
                <div>
                    {/* 筛选条件 */}
                    <div className="mb-4">
                        <div className="flex items-center mb-2">
                            <span className="text-sm font-medium text-gray-700 mr-2">{t('pickup.appointment.copyAppointmentModal.pickupDate')}:</span>
                            <span className="text-sm text-gray-900 font-medium bg-gray-50 px-2 py-1 rounded">
                                {listPickupDate || dayjs().format('YYYY-MM-DD')}
                            </span>
                            <span className="text-xs text-gray-500 ml-2">({t('pickup.appointment.copyAppointmentModal.fixedFromListFilter')})</span>
                        </div>
                        <PickupAppointmentFilter
                            filters={filters}
                            setFilters={setFilters}
                            handleFilter={handleFilter}
                            handleResetFilters={handleResetFilters}
                            tempFilters={tempFilters}
                            setTempFilters={setTempFilters}
                            disablePickupDate={true}
                        />
                        {/* 筛选和重置按钮 */}
                        <div className="flex justify-end gap-2 mt-4">
                            <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
                                <span className="font-bold">{t('pickup.appointment.buttons.filter')}</span>
                            </Button>
                            <Button onClick={handleResetFilters} className="w-auto min-w-24">
                                <span className="font-bold">{t('pickup.appointment.buttons.reset')}</span>
                            </Button>
                        </div>
                    </div>

                    {/* 已选择货量显示 */}
                    <div className="flex justify-between items-center text-sm text-gray-600">
                        <div>
                            {t('pickup.appointment.copyAppointmentModal.selectedQuantityOfGoods')}: {selectedSummary}
                        </div>
                    </div>

                    {/* 预约单列表 */}
                    <Table
                        rowSelection={rowSelection}
                        columns={columns}
                        dataSource={appointmentData}
                        rowKey="id"
                        loading={loading}
                        pagination={false}
                        scroll={{x: 1000}}
                        size="small"
                     className="mb-4"/>

                    {/* 分页 */}
                    <div className="flex justify-between items-center mt-4">
                        <div className="text-gray-500">{t('pickup.appointment.copyAppointmentModal.results', {count: totalElements})}</div>
                        <Pagination
                            current={currentPage}
                            total={totalElements}
                            pageSize={pageSize}
                            size="small"
                            showSizeChanger
                            onChange={handlePageChange}
                        />
                    </div>
                </div>
            )}

            {step === 'confirm' && (
                <div>
                    <Card title="Copy Configuration" className="mb-4">
                        <div className="space-y-4">
                            <div>
                                <div className="text-sm font-medium mb-2">{t('pickup.appointment.copyAppointmentModal.selectedAppointments')}:</div>
                                <div className="text-gray-600">
                                    {persistentSelection.length} {t('pickup.appointment.copyAppointmentModal.appointmentsSelected')}
                                </div>
                                <div className="text-sm text-gray-500">
                                    {t('pickup.appointment.copyAppointmentModal.totalGoods')}: {selectedSummary}
                                </div>
                            </div>
                            
                            <div>
                                <div className="text-sm font-medium mb-2">
                                    <span className="text-red-500 mr-1">*</span>
                                    {t('pickup.appointment.copyAppointmentModal.targetDates')}:
                                </div>
                                <div className="text-xs text-gray-500 mb-2">
                                    {t('pickup.appointment.copyAppointmentModal.selectDatesAfter', {date: listPickupDate || dayjs().format('YYYY-MM-DD')})}
                                </div>
                                <MultiDateSelector
                                    value={selectedDates}
                                    onChange={setSelectedDates}
                                    minimumDate={dayjs(listPickupDate || dayjs().format('YYYY-MM-DD')).add(1, 'day')}
                                />
                            </div>
                        </div>
                    </Card>
                </div>
            )}

            {step === 'result' && (
                <div>
                    <div className="mb-4">
                        <Space size="large">
                            <Statistic 
                                title={t('pickup.appointment.copyAppointmentModal.totalProcessed')}
                                value={copyResults.length} 
                            />
                            <Statistic 
                                title={t('pickup.appointment.copyAppointmentModal.totalSuccess')}
                                value={copyResults.reduce((sum, item) => sum + item.successCount, 0)} 
                                valueStyle={{color: '#52c41a'}}
                            />
                            <Statistic 
                                title={t('pickup.appointment.copyAppointmentModal.totalFailures')}
                                value={copyResults.reduce((sum, item) => sum + item.failureCount, 0)} 
                                valueStyle={{color: '#ff4d4f'}}
                            />
                        </Space>
                    </div>

                    <Table
                        columns={resultColumns}
                        dataSource={copyResults}
                        rowKey="appointmentNumber"
                        pagination={false}
                        size="small"
                     className="mb-4"/>
                </div>
            )}
            </Modal>
        </PickupDataProvider>
    );
};

export default PickupAppointmentCopyModal; 